import type { CompanyRunResearch } from "@/admin/monitoring/company-run-research-data"
import type { FileEvidenceTarget } from "@/admin/monitoring/file-evidence-data"
import { formatRunStart } from "@/admin/monitoring/run-presentation"
import { StoredValue } from "@/admin/monitoring/stored-value"
import { terminalControlVariants } from "@/components/terminal/control-variants"
import {
  AssessmentOutcome,
  dataByAssessmentOutcome,
} from "@/constants/terminal/assessment-outcome"

const assessmentLabel = (
  assessment: CompanyRunResearch["parts"]["assessment"]
) => {
  if (!assessment) return "No Assessment recorded"
  if (!assessment.outcome) return "Assessment in progress"
  return dataByAssessmentOutcome[assessment.outcome].label
}

// What the Run's Assessment did and when, with its error when it failed.
export const AssessmentSummary = ({
  assessment,
  onOpenEvidence,
}: {
  assessment: CompanyRunResearch["parts"]["assessment"]
  onOpenEvidence: (target: FileEvidenceTarget, title: string) => void
}) => (
  <div className="space-y-3">
    <h2 className="text-lg font-semibold">{assessmentLabel(assessment)}</h2>
    {assessment && (
      <p className="text-muted-foreground text-xs">
        Assessment started {formatRunStart(assessment.startedAt)}
        {assessment.completedAt
          ? ` and finished ${formatRunStart(assessment.completedAt)}.`
          : " and hasn’t finished."}
      </p>
    )}
    {assessment?.error && (
      <div className="border-assessment-critical space-y-2 rounded-sm border p-3">
        <StoredValue value={assessment.error} />
        <button
          className={terminalControlVariants({ size: "m" })}
          onClick={() =>
            onOpenEvidence({ kind: "assessment" }, "Assessment error")
          }
        >
          Show Assessment evidence
        </button>
      </div>
    )}
  </div>
)

// Without a completed Assessment, the File tab says why the Run shows none.
export const AssessmentStatus = ({
  assessment,
  onOpenEvidence,
}: {
  assessment: CompanyRunResearch["parts"]["assessment"]
  onOpenEvidence: (target: FileEvidenceTarget, title: string) => void
}) => (
  <section className="space-y-3 text-sm">
    <AssessmentSummary
      assessment={assessment}
      onOpenEvidence={onOpenEvidence}
    />
    {assessment?.outcome === AssessmentOutcome.ASSESSED && (
      <p className="text-muted-foreground">
        An assessment-only Run scores the Company without showing it to the
        buyer. The Assessment tab shows what this Run scored.
      </p>
    )}
  </section>
)
